import { useState, useEffect, useRef } from 'react';
import { api } from './api.js';
import { withLimit } from './limit.js';
import { useScope } from './ScopeContext.jsx';
import { ANY_VALUE } from './filterConstants.js';
import { lockFieldFor } from './tileLockFields.js';
import { combinedFiltersForTile } from './combinedFilters.js';
import { withMergeFilters } from './mergeFilters.js';

// A query is only worth running once it has a model, an explore (view) and at
// least one field — otherwise Looker returns a validation error. A tile backed
// by a PULSE dataset instead of Looker (today: a lead feed, server/leadFeeds.js)
// names its source and needs none of that; run-query serves it the same
// json_detail envelope, so everything downstream is unchanged.
export function isRunnableQuery(q) {
  if (q && q.source === 'lead_feed') return !!q.feedId;
  if (q && q.source === 'campaign_perf') return Array.isArray(q.fields) && q.fields.length > 0; // Pulse's own campaign numbers (server/campaignTiles.js)
  return !!(q && q.model && q.view && Array.isArray(q.fields) && q.fields.length > 0);
}

// Runs a tile's Looker query, re-running when the query or the active filter
// values change. Returns { data, loading, error }. Looker does the calculation;
// we only receive json_detail rows.
export function useTileData(tile, filterValues) {
  const { suiteId, refreshKey = 0, softKey = 0, tileLocks = {}, lockFilters = [], combinedLocks = [] } = useScope();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(tile.type !== 'text' && isRunnableQuery(tile.query));
  const [error, setError] = useState(null);
  // A tile's BLEND (Pulse data merged onto its Looker rows — server/leadFeeds.js)
  // is a query input like any other: change it and the tile must re-run.
  // What a blend does depends on what the tile SHOWS (a KPI card takes the
  // registrations onto its number; a chart lines them up on its date axis), so
  // the vis type rides along with the spec and is re-read on every run — a tile
  // switched from card to chart blends the right way without being re-saved.
  const blendSpec = tile.blend ? { ...tile.blend, visType: tile.vis?.type || '' } : null;
  const blendKey = blendSpec ? JSON.stringify(blendSpec) : '';
  const abortRef = useRef(null);
  const hasData = useRef(false);
  // Previous trigger inputs — to tell a HARD refresh (refreshKey, force a live
  // fetch) from a SOFT auto-refresh (softKey, on focus/interval — silent + cached)
  // from a normal change (query/filter/scope — show the skeleton).
  const prev = useRef({ queryKey: '', overrideKey: '', suiteId, refreshKey, softKey });

  // Build filter overrides for this tile from the dashboard-level filter values,
  // using the tile's listenTo wiring ({ filterName -> queryField }). A per-tile
  // lock (suite.tileLocks for this client) forces this ONE tile's value for a
  // filter, overriding the dashboard's value.
  const myLocks = tileLocks?.[tile.id] || {};
  const overrides = {};
  for (const [filterName, queryField] of Object.entries(tile.listenTo || {})) {
    const locked = myLocks[filterName];
    const val = (locked != null && String(locked).trim() !== '') ? locked : filterValues?.[filterName];
    // "Any value": send the ANY_VALUE sentinel so the server DROPS this field
    // from the query entirely (true "is any value"). An empty string wouldn't
    // work — Looker reads "" as "is blank", not "no constraint".
    if (val === ANY_VALUE) overrides[queryField] = ANY_VALUE;
    else if (val && String(val).trim()) overrides[queryField] = String(val).trim();
  }
  // A hand-added dashboard filter (no Looker model/explore, so it was never wired
  // into any tile's `listenTo`) still has to filter the report. Apply its value to
  // any tile whose own query already uses that field's view — the same field-match
  // the lock picker uses. Looker-imported filters carry model/explore and keep
  // their explicit listenTo wiring only, so nothing over-filters.
  for (const f of lockFilters || []) {
    if (f.model || f.explore) continue;                     // Looker-wired → listenTo only (above)
    if (tile.listenTo && f.name in tile.listenTo) continue; // already handled in Loop 1
    const val = filterValues?.[f.name];
    if (!val || val === ANY_VALUE || !String(val).trim()) continue;
    const queryField = lockFieldFor(tile, f.name, lockFilters);
    if (queryField) overrides[queryField] = String(val).trim();
  }
  // Per-tile locks on filters the tile ISN'T wired to via listenTo — resolve the
  // filter to a query field the tile's own query already uses, and apply it.
  for (const [filterName, locked] of Object.entries(myLocks)) {
    if (tile.listenTo && filterName in tile.listenTo) continue; // handled above
    if (locked == null || String(locked).trim() === '') continue;
    const queryField = lockFieldFor(tile, filterName, lockFilters);
    if (queryField) overrides[queryField] = String(locked).trim();
  }

  // Combined-field OR locks that apply to THIS tile (≥1 of their fields is in a
  // view the tile's query joins). Forwarded to the server, which turns them into a
  // Looker filter_expression (AND-combined with the scoped filters map).
  const myCombined = combinedFiltersForTile(combinedLocks, tile.query, ANY_VALUE, tile.listenTo);

  // A merged-results tile's other sources take the same dashboard filters
  // through their own wiring (lib/mergeFilters.js). Part of the query, so a
  // filter change re-runs the tile.
  const runQuery = withMergeFilters(tile.query, (name) => {
    const locked = myLocks[name];
    return (locked != null && String(locked).trim() !== '') ? locked : filterValues?.[name];
  });

  const queryKey = JSON.stringify(runQuery) + blendKey;
  const overrideKey = JSON.stringify(overrides);
  const combinedKey = JSON.stringify(myCombined);

  useEffect(() => {
    if (tile.type === 'text' || !isRunnableQuery(tile.query)) {
      setLoading(false);
      setData(null);
      setError(null);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const p = prev.current;
    // HARD refresh (Refresh button) → force a cache-bypassing live fetch.
    const force = refreshKey !== p.refreshKey;
    // SOFT auto-refresh (focus / interval): only softKey changed, we already have
    // data → re-fetch quietly via the cache, no skeleton flash.
    const softOnly = !force && p.queryKey === queryKey && p.overrideKey === overrideKey && p.combinedKey === combinedKey && p.suiteId === suiteId && softKey !== p.softKey && hasData.current;
    prev.current = { queryKey, overrideKey, combinedKey, suiteId, refreshKey, softKey };

    if (!softOnly) setLoading(true);
    setError(null);

    withLimit(() => api.runQuery(runQuery, overrides, controller.signal, suiteId, force, myCombined, blendSpec))
      .then((d) => { setData(d); hasData.current = true; })
      .catch((err) => {
        if (err.name !== 'AbortError') setError(err.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tile.type, queryKey, overrideKey, combinedKey, suiteId, refreshKey, softKey]);

  return { data, loading, error };
}
